import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminAuthApi, cseMarketApi } from '../services/api.js';
import DataTable from '../components/DataTable.jsx';

// ── Design-system class constants ───────────────────────────────────────────
// Mirrors AdminPaymentsPage.jsx so this page looks native to the rest of the
// admin panel instead of inventing its own visual language.

const cardClass = 'rounded-xl border border-slate-200 bg-white shadow-sm';
const btnPrimary = 'inline-flex items-center justify-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50';
const selectClass = 'rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition-colors focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20';
const chipClass = (active) =>
  `rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
    active ? 'border-indigo-600 bg-indigo-600 text-white' : 'border-slate-300 bg-white text-slate-600 hover:border-indigo-300 hover:text-indigo-600'
  }`;

const BADGE_TONES = {
  success: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  warning: 'bg-amber-50 text-amber-700 border-amber-200',
  danger: 'bg-red-50 text-red-700 border-red-200',
  neutral: 'bg-slate-100 text-slate-600 border-slate-200',
};

const Badge = ({ tone = 'neutral', children }) => (
  <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium whitespace-nowrap ${BADGE_TONES[tone] || BADGE_TONES.neutral}`}>
    {children}
  </span>
);

const PageHeader = ({ title, subtitle, children }) => (
  <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
    <div>
      <h1 className="text-2xl font-semibold text-slate-900">{title}</h1>
      {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
    </div>
    {children && <div className="flex items-center gap-2">{children}</div>}
  </div>
);

// ── Icons ────────────────────────────────────────────────────────────────

const IconRefresh = () => (
  <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 flex-shrink-0">
    <path fillRule="evenodd" d="M15.312 5.312a5.5 5.5 0 00-9.201 2.466.75.75 0 11-1.453-.377 7 7 0 0111.712-3.138l.766-.766a.75.75 0 011.28.53V7.5a.75.75 0 01-.75.75h-3.483a.75.75 0 01-.53-1.28l.659-.658zM3.938 13.688a.75.75 0 01-1.28-.53V9.5a.75.75 0 01.75-.75h3.483a.75.75 0 01.53 1.28l-.659.658a5.5 5.5 0 009.201-2.466.75.75 0 111.453.377 7 7 0 01-11.712 3.138l-.766.766z" clipRule="evenodd" />
  </svg>
);

const IconBack = () => (
  <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 flex-shrink-0">
    <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
  </svg>
);

const IconSparkle = () => (
  <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 flex-shrink-0">
    <path d="M9 4.5a.5.5 0 01.947-.22l.894 1.79 1.79.895a.5.5 0 010 .894l-1.79.894-.894 1.79a.5.5 0 01-.894 0l-.894-1.79-1.79-.894a.5.5 0 010-.894l1.79-.894L9 4.5zM4 12.5a.5.5 0 01.894-.31l.5 1 1 .5a.5.5 0 010 .894l-1 .5-.5 1a.5.5 0 01-.894 0l-.5-1-1-.5a.5.5 0 010-.894l1-.5.5-1z" />
  </svg>
);

// ── Formatters ───────────────────────────────────────────────────────────

const fmtNum = (value, decimals = 2) =>
  value === null || value === undefined || Number.isNaN(Number(value))
    ? '-'
    : new Intl.NumberFormat('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(value);

const fmtInt = (value) => (value === null || value === undefined ? '-' : new Intl.NumberFormat('en-US').format(value));

const fmtDateTime = (value) => {
  if (!value) return '-';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '-' : parsed.toLocaleString();
};

const CDN_BASE = 'https://cdn.cse.lk/cmt/';

const ChangeCell = ({ value, percent }) => {
  const tone = value > 0 ? 'text-emerald-600' : value < 0 ? 'text-red-600' : 'text-slate-500';
  return (
    <span className={`font-medium ${tone}`}>
      {value > 0 ? '+' : ''}{fmtNum(value)}
      {percent !== undefined && percent !== null && (
        <span className="ml-1 text-xs">({value > 0 ? '+' : ''}{fmtNum(percent)}%)</span>
      )}
    </span>
  );
};

const CompanyCell = ({ name, logoUrl }) => {
  const [broken, setBroken] = useState(false);
  const initials = (name || '?').trim().charAt(0).toUpperCase();
  return (
    <div className="flex items-center gap-2.5 min-w-[180px]">
      {logoUrl && !broken ? (
        <img
          src={`${CDN_BASE}${logoUrl}`}
          alt=""
          onError={() => setBroken(true)}
          className="h-6 w-6 flex-shrink-0 rounded-full object-contain border border-slate-100 bg-white"
        />
      ) : (
        <span className="h-6 w-6 flex-shrink-0 rounded-full bg-indigo-50 text-indigo-600 text-[10px] font-bold flex items-center justify-center">
          {initials}
        </span>
      )}
      <span className="font-medium text-slate-900 whitespace-nowrap">{name || '-'}</span>
    </div>
  );
};

const formatRunAt = (value) => {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '-' : parsed.toLocaleString();
};

const TABS = [
  { id: 'trade-summary', label: 'Trade Summary' },
  { id: 'detailed-trades', label: 'Detailed Trades' },
  { id: 'market-cap', label: 'Market Capitalization' },
  { id: 'gics-summary', label: 'GICS Industry Summary' },
];

// ── Column definitions ──────────────────────────────────────────────────
// Every column here is sortable/filterable/exportable for free via DataTable —
// only rendering (`render`) needs a custom cell (logos, colored change badges).

const TRADE_SUMMARY_COLUMNS = [
  { key: 'name', label: 'Company Name', render: (r) => <CompanyCell name={r.name} logoUrl={r.logoUrl} /> },
  { key: 'symbol', label: 'Symbol' },
  { key: 'price', label: 'Price', align: 'right', render: (r) => fmtNum(r.price) },
  {
    key: 'change', label: 'Change', align: 'right',
    render: (r) => <ChangeCell value={r.change} percent={r.percentageChange} />,
    sortValue: (r) => r.change, exportValue: (r) => r.change,
  },
  { key: 'open', label: 'Open', align: 'right', render: (r) => fmtNum(r.open) },
  { key: 'high', label: 'High', align: 'right', render: (r) => fmtNum(r.high) },
  { key: 'low', label: 'Low', align: 'right', render: (r) => fmtNum(r.low) },
  { key: 'previousClose', label: 'Prev Close', align: 'right', render: (r) => fmtNum(r.previousClose) },
  { key: 'sharevolume', label: 'Volume', align: 'right', render: (r) => fmtInt(r.sharevolume) },
  { key: 'turnover', label: 'Turnover', align: 'right', render: (r) => fmtNum(r.turnover) },
  { key: 'marketCap', label: 'Market Cap', align: 'right', render: (r) => fmtInt(r.marketCap) },
  {
    key: 'lastTradedTime', label: 'Date / Time',
    render: (r) => (r.lastTradedTime ? fmtDateTime(r.lastTradedTime) : r.date),
    exportValue: (r) => (r.lastTradedTime ? fmtDateTime(r.lastTradedTime) : r.date),
  },
];

// The CSE detailedTrades feed is per-tick (a symbol appears once per trade),
// and "Trade Volume" (number of trades in that tick) / "Share Volume" (shares
// in that tick) were previously mislabeled/swapped as "Trades"/"Qty".
const DETAILED_TRADES_COLUMNS = [
  { key: 'name', label: 'Company Name', render: (r) => <CompanyCell name={r.name} logoUrl={r.logoUrl} /> },
  { key: 'symbol', label: 'Symbol' },
  { key: 'price', label: 'Price', align: 'right', render: (r) => fmtNum(r.price) },
  { key: 'trades', label: 'Trade Volume', align: 'right', render: (r) => fmtInt(r.trades) },
  { key: 'qty', label: 'Share Volume', align: 'right', render: (r) => fmtInt(r.qty) },
  {
    key: 'change', label: 'Change (Rs.)', align: 'right',
    render: (r) => <ChangeCell value={r.change} />, sortValue: (r) => r.change, exportValue: (r) => r.change,
  },
  { key: 'changePercentage', label: 'Change (%)', align: 'right', render: (r) => fmtNum(r.changePercentage) },
  { key: 'date', label: 'Date' },
];

const MARKET_CAP_COLUMNS = [
  { key: 'name', label: 'Company Name', render: (r) => <CompanyCell name={r.name} logoUrl={r.logoUrl} /> },
  { key: 'symbol', label: 'Symbol' },
  { key: 'price', label: 'Price', align: 'right', render: (r) => fmtNum(r.price) },
  {
    key: 'change', label: 'Change', align: 'right',
    render: (r) => <ChangeCell value={r.change} percent={r.percentageChange} />,
    sortValue: (r) => r.change, exportValue: (r) => r.change,
  },
  { key: 'marketCap', label: 'Market Cap', align: 'right', render: (r) => fmtInt(r.marketCap) },
  { key: 'marketCapPercentage', label: 'Mkt Cap %', align: 'right', render: (r) => fmtNum(r.marketCapPercentage) },
  { key: 'issuedQTY', label: 'Issued Qty', align: 'right', render: (r) => fmtInt(r.issuedQTY) },
  { key: 'sharevolume', label: 'Volume', align: 'right', render: (r) => fmtInt(r.sharevolume) },
  { key: 'turnover', label: 'Turnover', align: 'right', render: (r) => fmtNum(r.turnover) },
  {
    key: 'lastTradedTime', label: 'Date / Time',
    render: (r) => (r.lastTradedTime ? fmtDateTime(r.lastTradedTime) : r.date),
    exportValue: (r) => (r.lastTradedTime ? fmtDateTime(r.lastTradedTime) : r.date),
  },
];

const GICS_COLUMNS = [
  { key: 'indexName', label: 'Industry Group' },
  { key: 'name', label: 'Sector' },
  { key: 'indexValue', label: 'Index Value', align: 'right', render: (r) => fmtNum(r.indexValue) },
  {
    key: 'change', label: 'Change', align: 'right',
    render: (r) => <ChangeCell value={r.change} percent={r.percentage} />,
    sortValue: (r) => r.change, exportValue: (r) => r.change,
  },
  { key: 'sectorTradeToday', label: 'Trades', align: 'right', render: (r) => fmtInt(r.sectorTradeToday) },
  { key: 'sectorVolumeToday', label: 'Volume', align: 'right', render: (r) => fmtInt(r.sectorVolumeToday) },
  { key: 'sectorTurnoverToday', label: 'Turnover', align: 'right', render: (r) => fmtNum(r.sectorTurnoverToday) },
  { key: 'date', label: 'Date' },
];

const AdminCseMarketPage = () => {
  // Auth (session is shared across /admin/* — verified the same way as AdminPaymentsPage)
  const [authChecked, setAuthChecked] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const [activeTab, setActiveTab] = useState('trade-summary');
  const [board, setBoard] = useState('EQUITY');
  const [error, setError] = useState('');

  const [availableDates, setAvailableDates] = useState([]);
  // A single day is just a range where both ends match — the backend treats
  // {startDate, endDate} as an inclusive range either way.
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [tradeSummary, setTradeSummary] = useState([]);
  const [detailedTrades, setDetailedTrades] = useState([]);
  const [gicsSummary, setGicsSummary] = useState([]);
  const [marketCap, setMarketCap] = useState([]);
  const [loading, setLoading] = useState(false);

  const [statusLogs, setStatusLogs] = useState([]);
  const [fetchingNow, setFetchingNow] = useState(false);

  useEffect(() => {
    if (authChecked) return;
    (async () => {
      try {
        const res = await adminAuthApi.getCurrentUser();
        if (res.data?.authenticated) setIsAuthenticated(true);
      } catch {}
      finally { setAuthChecked(true); }
    })();
  }, [authChecked]);

  const loadStatus = async () => {
    try {
      const res = await cseMarketApi.getStatus();
      setStatusLogs(res.data?.logs || []);
    } catch {}
  };

  const loadAvailableDates = async () => {
    try {
      const res = await cseMarketApi.getAvailableDates();
      setAvailableDates(res.data?.dates || []);
    } catch {}
  };

  // When neither end of the range is set yet (first load), omit date params
  // entirely so the backend falls back to the most recent trading day.
  const dateParams = dateFrom || dateTo ? { startDate: dateFrom || dateTo, endDate: dateTo || dateFrom } : undefined;

  const loadTab = async (tab) => {
    setLoading(true);
    setError('');
    try {
      if (tab === 'trade-summary') {
        const res = await cseMarketApi.getTradeSummary(dateParams);
        setTradeSummary(res.data?.rows || []);
        if (!dateFrom && !dateTo && res.data?.date) { setDateFrom(res.data.date); setDateTo(res.data.date); }
      } else if (tab === 'detailed-trades') {
        const res = await cseMarketApi.getDetailedTrades(board, dateParams);
        setDetailedTrades(res.data?.rows || []);
        if (!dateFrom && !dateTo && res.data?.date) { setDateFrom(res.data.date); setDateTo(res.data.date); }
      } else if (tab === 'market-cap') {
        const res = await cseMarketApi.getMarketCap(dateParams);
        setMarketCap(res.data?.rows || []);
        if (!dateFrom && !dateTo && res.data?.date) { setDateFrom(res.data.date); setDateTo(res.data.date); }
      } else if (tab === 'gics-summary') {
        const res = await cseMarketApi.getGicsSummary(dateParams);
        setGicsSummary(res.data?.rows || []);
        if (!dateFrom && !dateTo && res.data?.date) { setDateFrom(res.data.date); setDateTo(res.data.date); }
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAuthenticated) return;
    loadAvailableDates();
    loadStatus();
  }, [isAuthenticated]); // eslint-disable-line

  useEffect(() => {
    if (!isAuthenticated) return;
    loadTab(activeTab);
  }, [isAuthenticated, activeTab, board, dateFrom, dateTo]); // eslint-disable-line

  const oldestAvailable = availableDates[availableDates.length - 1] || '';
  const latestAvailable = availableDates[0] || '';

  const setRange = (from, to) => {
    setDateFrom(from);
    setDateTo(to);
  };

  const fetchNow = async () => {
    setFetchingNow(true);
    setError('');
    try {
      await cseMarketApi.fetchNow();
      await loadStatus();
      await loadAvailableDates();
      await loadTab(activeTab);
    } catch (err) {
      setError(err.response?.data?.error || 'Manual fetch failed.');
    } finally {
      setFetchingNow(false);
    }
  };

  const latestLog = statusLogs[0];
  const asOfLabel = dateFrom && dateTo && dateFrom !== dateTo ? `${dateFrom} to ${dateTo}` : (dateFrom || dateTo || '');
  const filenameDateSuffix = dateFrom && dateTo && dateFrom !== dateTo ? `${dateFrom}_to_${dateTo}` : (dateFrom || dateTo || 'latest');

  // ── Render: session loading ─────────────────────────────────────────────

  if (!authChecked) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex items-center gap-3 text-sm text-slate-500">
          <svg className="h-4 w-4 animate-spin text-indigo-500" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          Checking session…
        </div>
      </div>
    );
  }

  // ── Render: not signed in ───────────────────────────────────────────────

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-50 text-slate-900 flex items-center justify-center px-4">
        <div className="w-full max-w-sm text-center">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-indigo-600 text-lg font-bold text-white shadow-sm mb-3 mx-auto">J</div>
          <p className="text-xl font-semibold tracking-tight text-slate-900 mb-1">CSE Market Data</p>
          <p className="text-slate-500 text-sm mb-6">Sign in to the admin dashboard to view this page.</p>
          <Link to="/admin/payments" className={`inline-flex ${btnPrimary}`}>Go to Sign In</Link>
        </div>
      </div>
    );
  }

  // ── Render: dashboard ────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-4">
          <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-indigo-600 text-sm font-bold text-white">J</div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-slate-900">CSE Market Data</p>
            <p className="text-[11px] text-slate-400 uppercase tracking-widest">Admin</p>
          </div>
          <Link to="/admin/ai-analysis" className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-indigo-600">
            <IconSparkle /> AI Document Analysis
          </Link>
          <Link to="/admin/payments" className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-indigo-600">
            <IconBack /> Payments Admin
          </Link>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {error && (
          <div className="mb-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700 text-sm">{error}</div>
        )}

        {/* Status strip */}
        <div className={`${cardClass} p-4 mb-6 flex flex-wrap items-center justify-between gap-4`}>
          <div className="flex flex-wrap items-center gap-4 text-sm">
            <div>
              <span className="text-slate-500">Last run: </span>
              <span className="font-medium text-slate-800">{latestLog ? formatRunAt(latestLog.runAt) : 'Never'}</span>
              {latestLog && (
                <Badge tone={latestLog.overallStatus === 'SUCCESS' ? 'success' : latestLog.overallStatus === 'PARTIAL' ? 'warning' : 'danger'}>
                  {latestLog.overallStatus} · {latestLog.trigger}
                </Badge>
              )}
            </div>
            {latestLog?.results?.some((r) => r.status === 'FAILED') && (
              <div className="text-xs text-red-600">
                {latestLog.results.filter((r) => r.status === 'FAILED').map((r) => r.source).join(', ')} failed
              </div>
            )}
          </div>
          <button type="button" onClick={fetchNow} disabled={fetchingNow} className={btnPrimary}>
            <IconRefresh /> {fetchingNow ? 'Fetching…' : 'Fetch Now'}
          </button>
        </div>

        <PageHeader title="CSE Market Data" subtitle="Colombo Stock Exchange data, refreshed daily after market close (4:05pm Sri Lanka time)">
          <div className="flex flex-wrap items-center gap-2">
            <label className="text-sm text-slate-500" htmlFor="cse-date-from">From</label>
            <input
              id="cse-date-from"
              type="date"
              value={dateFrom}
              min={oldestAvailable || undefined}
              max={dateTo || latestAvailable || undefined}
              onChange={(e) => setDateFrom(e.target.value)}
              className={selectClass}
            />
            <label className="text-sm text-slate-500" htmlFor="cse-date-to">To</label>
            <input
              id="cse-date-to"
              type="date"
              value={dateTo}
              min={dateFrom || oldestAvailable || undefined}
              max={latestAvailable || undefined}
              onChange={(e) => setDateTo(e.target.value)}
              className={selectClass}
            />
            {latestAvailable && dateTo !== latestAvailable && (
              <button type="button" onClick={() => setRange(latestAvailable, latestAvailable)} className={chipClass(false)}>
                Latest
              </button>
            )}
          </div>
        </PageHeader>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-5">
          {TABS.map((t) => (
            <button key={t.id} type="button" onClick={() => setActiveTab(t.id)} className={chipClass(activeTab === t.id)}>
              {t.label}
            </button>
          ))}
        </div>

        {activeTab === 'detailed-trades' && (
          <div className="flex gap-2 mb-4">
            {['EQUITY', 'DEBT'].map((b) => (
              <button key={b} type="button" onClick={() => setBoard(b)} className={chipClass(board === b)}>
                {b === 'EQUITY' ? 'Equity' : 'Debt'}
              </button>
            ))}
          </div>
        )}

        {/* ════ TRADE SUMMARY ═══════════════════════════════════════════ */}
        {activeTab === 'trade-summary' && (
          <DataTable
            columns={TRADE_SUMMARY_COLUMNS}
            rows={tradeSummary}
            loading={loading}
            emptyLabel="No trade summary data for this range."
            asOf={asOfLabel}
            filename={`cse-trade-summary-${filenameDateSuffix}`}
            getRowKey={(r) => `${r.date}-${r.symbol}`}
          />
        )}

        {/* ════ DETAILED TRADES ═════════════════════════════════════════ */}
        {activeTab === 'detailed-trades' && (
          <DataTable
            columns={DETAILED_TRADES_COLUMNS}
            rows={detailedTrades}
            loading={loading}
            emptyLabel={`No ${board.toLowerCase()} detailed trades for this range.`}
            asOf={asOfLabel}
            filename={`cse-detailed-trades-${board.toLowerCase()}-${filenameDateSuffix}`}
            getRowKey={(r) => `${r.date}-${r.symbol}-${r.seq}`}
          />
        )}

        {/* ════ MARKET CAPITALIZATION ═══════════════════════════════════ */}
        {activeTab === 'market-cap' && (
          <DataTable
            columns={MARKET_CAP_COLUMNS}
            rows={marketCap}
            loading={loading}
            emptyLabel="No market capitalization data for this range."
            asOf={asOfLabel}
            filename={`cse-market-cap-${filenameDateSuffix}`}
            getRowKey={(r) => `${r.date}-${r.symbol}`}
          />
        )}

        {/* ════ GICS INDUSTRY SUMMARY ═══════════════════════════════════ */}
        {activeTab === 'gics-summary' && (
          <DataTable
            columns={GICS_COLUMNS}
            rows={gicsSummary}
            loading={loading}
            emptyLabel="No GICS industry group data for this range."
            asOf={asOfLabel}
            filename={`cse-gics-summary-${filenameDateSuffix}`}
            getRowKey={(r) => `${r.date}-${r.indexName}`}
          />
        )}
      </main>
    </div>
  );
};

export default AdminCseMarketPage;
